import { pauseWindowErrorMessage } from "@redotech/admin-sdk/coverage/pause-window-error-message";
import {
  MAX_MERCHANT_FEATURE_PAUSE_MS,
  validatePauseWindow,
} from "@redotech/redo-merchant-pause-common/paused-feature";
import { dateToPlainDate } from "@redotech/redo-web/arbiter-components/time/time-zone-conversion";
import { plainDateToCurrentDate } from "@redotech/util/temporal";

export interface CoveragePauseDraft {
  /** Calendar day the pause starts: right away if today, else start of day. */
  startDate: Temporal.PlainDate;
  /** Last calendar day of the pause; coverage resumes at the end of it. */
  endDate: Temporal.PlainDate;
}

export interface CoveragePauseRequest {
  /** Undefined when the pause starts right away, so the server stamps `now`. */
  startsAt: Date | undefined;
  endsAt: Date;
}

export type CoveragePauseWindow =
  | { request: CoveragePauseRequest; error: null }
  | { request: null; error: string };

/** When a pause picked to start on `startDate` begins; undefined means now. */
export function pauseStartInstant(
  startDate: Temporal.PlainDate,
  now: Date,
  timeZone: string,
): Date | undefined {
  const today = dateToPlainDate(now, timeZone);
  return Temporal.PlainDate.compare(startDate, today) > 0
    ? plainDateToCurrentDate(startDate, timeZone)
    : undefined;
}

/**
 * Latest end day for a pause starting at `startsAt`. Days are wall-clock in
 * `timeZone` but the limit is measured in elapsed time (the DB check adds a
 * fixed interval), so a day that crosses a DST change can shorten the span.
 */
export function latestPauseEndDate(
  startsAt: Date,
  timeZone: string,
): Temporal.PlainDate {
  const latestEnd = startsAt.getTime() + MAX_MERCHANT_FEATURE_PAUSE_MS;
  let endDate = dateToPlainDate(new Date(latestEnd), timeZone);
  while (
    plainDateToCurrentDate(endDate.add({ days: 1 }), timeZone).getTime() >
    latestEnd
  ) {
    endDate = endDate.subtract({ days: 1 });
  }
  return endDate;
}

export function latestPauseEndDateFor(
  startDate: Temporal.PlainDate,
  now: Date,
  timeZone: string = Temporal.Now.timeZoneId(),
): Temporal.PlainDate {
  return latestPauseEndDate(
    pauseStartInstant(startDate, now, timeZone) ?? now,
    timeZone,
  );
}

/**
 * Turns the calendar-day picks from the pause form into the instants the RPC
 * takes, in the admin's time zone, and reports why they are not allowed.
 */
export function coveragePauseWindow(
  draft: CoveragePauseDraft,
  now: Date,
  timeZone: string = Temporal.Now.timeZoneId(),
): CoveragePauseWindow {
  const today = dateToPlainDate(now, timeZone);
  if (Temporal.PlainDate.compare(draft.startDate, today) < 0) {
    return { request: null, error: "The start date cannot be in the past." };
  }
  const startsAt = pauseStartInstant(draft.startDate, now, timeZone);
  const endsAt = plainDateToCurrentDate(
    draft.endDate.add({ days: 1 }),
    timeZone,
  );
  const windowError = validatePauseWindow(
    { startsAt: startsAt ?? now, endsAt },
    now,
  );
  if (windowError) {
    return { request: null, error: pauseWindowErrorMessage(windowError) };
  }
  return { request: { startsAt, endsAt }, error: null };
}
